import type Konva from "konva";
import { layoutCanvasPoint, layoutLocalPoint } from "../../lib/schedule-art/layout-coordinates";
import { ART_HEIGHT, ART_WIDTH } from "../../lib/schedule-art/art-canvas";
import { useMetadataEditingStore } from "../../stores/metadata-editing-store";
import { dayBounds, metadataBounds, metadataNodes, type Bounds } from "./metadata-bounds";

export type AlignmentGuide = { axis: "x" | "y"; value: number };

function anchors(bounds: Bounds, axis: "x" | "y") {
  const size = axis === "x" ? bounds.width : bounds.height;
  return [bounds[axis], bounds[axis] + size / 2, bounds[axis] + size];
}

export function snapMetadataNode(node: Konva.Group, origin: number[], bypass: boolean) {
  const stage = node.getStage()!;
  const guides: AlignmentGuide[] = [];
  const rotation = node.getAttr("metadataLayoutRotation") as number | undefined;
  const round = () => {
    const local = layoutLocalPoint([node.x(), node.y()], origin, rotation).map(Math.round);
    const canvas = layoutCanvasPoint(local, origin, rotation);
    node.position({ x: canvas[0]!, y: canvas[1]! });
  };
  round();
  node.setAttr("metadataGuides", guides);
  if (bypass || !useMetadataEditingStore.getState().snapping) return;
  const isDay = (node.getAttr("metadataPath") as string).endsWith(".origin");
  const nodes = metadataNodes(stage).filter(other => other !== node && other.getClientRect().width > 0);
  let candidates: Bounds[];
  if (isDay) {
    const otherDays = new Set<string>(nodes.map(other => other.getAttr("metadataDay")).filter(Boolean));
    otherDays.delete(node.getAttr("metadataDay"));
    candidates = [...otherDays].flatMap(day => {
      const bounds = dayBounds(stage, day);
      return bounds ? [bounds] : [];
    });
  } else {
    candidates = nodes.map(other => metadataBounds(other));
  }
  candidates.push({ x: 0, y: 0, width: ART_WIDTH, height: ART_HEIGHT });
  const bounds = metadataBounds(node);
  const threshold = 6 / stage.scaleX();
  for (const axis of ["x", "y"] as const) {
    let closest = threshold;
    let delta = 0;
    let guide: number | undefined;
    for (const candidate of candidates) {
      for (const value of anchors(candidate, axis)) {
        for (const own of anchors(bounds, axis)) {
          const distance = Math.abs(value - own);
          if (distance >= closest) continue;
          closest = distance;
          delta = value - own;
          guide = value;
        }
      }
    }
    node[axis](node[axis]() + delta);
    if (guide !== undefined) guides.push({ axis, value: guide });
  }
  round();
}
